/** Display timezone for consumer/vendor event clocks (matches crawler + product locale). */
export const DISPLAY_TIMEZONE = "Europe/Zurich";

function zurichTimeParts(date: Date): { hour: string; minute: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  let hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  // Some engines emit "24" for midnight under hour12: false
  if (hour === "24") hour = "00";
  return { hour, minute };
}

/** Format a DB timestamptz / ISO string as HH:MM in Europe/Zurich. Bare HH:MM passes through. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const trimmed = iso.trim();

  const timeOnly = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (timeOnly) {
    const hour = timeOnly[1];
    const minute = timeOnly[2];
    if (hour === undefined || minute === undefined) return "—";
    return `${hour.padStart(2, "0")}:${minute}`;
  }

  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return "—";

  const { hour, minute } = zurichTimeParts(d);
  return `${hour}:${minute}`;
}

/** Event end time from ends_at, or starts_at + 4 hours when ends_at is missing. */
export function formatEventEndTime(
  startsAtIso: string | null | undefined,
  endsAtIso: string | null | undefined,
): string {
  if (endsAtIso) return formatTime(endsAtIso);
  if (!startsAtIso) return "—";

  const start = new Date(startsAtIso.trim());
  if (Number.isNaN(start.getTime())) return "—";

  const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);
  return formatTime(end.toISOString());
}

/**
 * Formats entry price for feed/map payloads.
 * Note: zero/null returns the English literal "Free" (not i18n) — callers that
 * surface this string in UI should prefer a translated free label.
 */
export function formatPrice(
  entryPrice: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (entryPrice == null || entryPrice === 0) return "Free";
  const cur = currency?.trim() || "";
  return cur ? `${cur} ${entryPrice}` : String(entryPrice);
}

export function formatSubscriptionDate(iso: string): string {
  return new Intl.DateTimeFormat("de-CH", {
    dateStyle: "medium",
    timeZone: DISPLAY_TIMEZONE,
  }).format(new Date(iso));
}
